
import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PuttSituation } from "@/types/golf";
import { useSavePuttingResults } from "@/hooks/useSavePuttingResults";
import { usePuttingAutoSave } from "@/hooks/putting/usePuttingAutoSave";
import PuttingScoreButtons from "./PuttingScoreButtons";
import PuttingScoreTable from "./PuttingScoreTable";
import CurrentPuttingPoint from "./CurrentPuttingPoint";
import { useTranslation } from "react-i18next";

interface PuttingTestProps {
  situation: PuttSituation;
  onClose: () => void;
  refreshAfterToast?: () => void;
}

const PuttingTestContainer = ({ situation, onClose, refreshAfterToast }: PuttingTestProps) => {
  const { t: tTraining } = useTranslation('training');
  const { t: tCommon } = useTranslation('common');
  const [scores, setScores] = useState<Record<string, number[]>>({});
  const [currentPoint, setCurrentPoint] = useState<string>(situation.distances[0].point);
  const [currentAttempt, setCurrentAttempt] = useState<number>(0);

  // Auto-save hook for persistence
  const { clearAutoSave } = usePuttingAutoSave({
    situation,
    scores,
    currentPoint,
    currentAttempt,
    setScores,
    setCurrentPoint,
    setCurrentAttempt
  });
  
  const { saveResults, getTotalPoints, getCurrentIndex } = useSavePuttingResults({
    situation,
    scores,
    onClose: () => {
      clearAutoSave();
      onClose();
    },
    refreshAfterToast
  });

  // Map situation ID to translation key
  const situationKeys: Record<number, string> = {
    1: 'puttingTraining.exercises.longPutts',
    2: 'puttingTraining.exercises.mediumPutts',
    3: 'puttingTraining.exercises.slopePutts',
    4: 'puttingTraining.exercises.offensivePutts',
    5: 'puttingTraining.exercises.shortPutts1m',
    6: 'puttingTraining.exercises.shortPutts2m',
  };
  
  const translationKey = situationKeys[situation.id];
  const translatedName = translationKey ? tCommon(`${translationKey}.name`) : situation.name;

  const handleScore = (points: number) => {
    setScores((prev) => ({
      ...prev,
      [currentPoint]: [...(prev[currentPoint] || []), points],
    }));

    const maxAttempts = situation.distances[0].ballCount;
    if (currentAttempt < maxAttempts - 1) {
      setCurrentAttempt(currentAttempt + 1);
    } else {
      setCurrentAttempt(0);
      const currentIndex = situation.distances.findIndex((d) => d.point === currentPoint);
      if (currentIndex < situation.distances.length - 1) {
        setCurrentPoint(situation.distances[currentIndex + 1].point);
      }
    }
  };

  const undoLastScore = () => {
    setScores((prev) => {
      const newScores = { ...prev };
      if (newScores[currentPoint]?.length) {
        newScores[currentPoint] = newScores[currentPoint].slice(0, -1);
        if (newScores[currentPoint].length === 0) {
          delete newScores[currentPoint];
        }
        if (currentAttempt > 0) {
          setCurrentAttempt(currentAttempt - 1);
        } else {
          const currentIndex = situation.distances.findIndex((d) => d.point === currentPoint);
          if (currentIndex > 0) {
            setCurrentPoint(situation.distances[currentIndex - 1].point);
            setCurrentAttempt(situation.distances[0].ballCount - 1);
          }
        }
        return newScores;
      }
      const currentIndex = situation.distances.findIndex((d) => d.point === currentPoint);
      if (currentIndex > 0) {
        const previousPoint = situation.distances[currentIndex - 1].point;
        if (newScores[previousPoint]?.length) {
          newScores[previousPoint] = newScores[previousPoint].slice(0, -1);
          if (newScores[previousPoint].length === 0) {
            delete newScores[previousPoint];
          }
          setCurrentPoint(previousPoint);
          setCurrentAttempt(situation.distances[0].ballCount - 1);
        }
      }
      return newScores;
    });
  };

  const getPointTotal = (point: string) => {
    const pointScores = scores[point] || [];
    return pointScores.reduce((sum, score) => sum + score, 0);
  };

  const totalPoints = getTotalPoints();
  const currentIndex = getCurrentIndex(totalPoints);

  const maxAttempts = situation.distances[0].ballCount;
  const isTestComplete = Object.keys(scores).length === situation.distances.length && 
                        Object.values(scores).every(pointScores => pointScores.length === maxAttempts);
  const hasScores = Object.keys(scores).length > 0;

  const currentDistance = situation.distances.find((d) => d.point === currentPoint)?.distance || 0;

  return (
    <Card className="w-full max-w-4xl mx-auto mt-2 md:mt-6 px-2 md:px-6">
      <CardHeader className="pb-2 md:pb-6">
        <div className="flex justify-between items-center mb-2">
          <Button 
            variant="outline" 
            asChild
          >
            <a href="/putting" className="flex items-center gap-2">
              <ChevronLeft className="h-4 w-4" />
              {tTraining('putting.backToTest')}
            </a>
          </Button>
        </div>
        <CardTitle className="text-xl md:text-2xl text-golf-700">{translatedName}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 md:space-y-6">
          <CurrentPuttingPoint 
            currentPoint={currentPoint}
            currentAttempt={currentAttempt}
            maxAttempts={maxAttempts}
            distance={currentDistance}
            undoLastScore={undoLastScore}
            hasScores={hasScores}
          />

          <PuttingScoreButtons 
            scoring={situation.scoring}
            onScoreSelect={handleScore}
            isTestComplete={isTestComplete}
          />

          <PuttingScoreTable 
            situation={situation}
            scores={scores}
            getPointTotal={getPointTotal}
            totalPoints={totalPoints}
            currentIndex={currentIndex}
            maxAttempts={maxAttempts}
          />

          <div className="flex justify-end mt-4">
            <button 
              onClick={saveResults} 
              className="w-full md:w-auto bg-white hover:bg-gray-100 text-golf-700 border border-golf-600 rounded px-4 py-2 text-sm md:text-base"
            >
              {tTraining('putting.finish')}
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PuttingTestContainer;
